import { useState } from 'react'
import dynamic from 'next/dynamic'

import { Trans, useTranslation } from '@mc/i18n'
import {
  Icon,
  Separator,
  Tile,
  TileImage,
  TileCaption,
} from '@mc/design-system'
import { getShareLessonProperties, ShareButton } from '@mc/share'
import { TRACK_CONTENT_TYPES } from '@mc/track-event'
import { useStaticData } from '@mc/static-data-react'

import Image from 'components/LegacyImage'
import Link from 'components/Link'
import CTA from 'components/CTA'
import { coursePath } from 'libs/paths'

import Ratings from '../Ratings'

const VideoModal = dynamic(() => import('components/VideoModal'), {
  ssr: false,
})

const Hero = () => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { chapter, course } = useStaticData()
  const { t } = useTranslation('@mc/next')

  const toggleTrailerModal = () => {
    setIsModalOpen(!isModalOpen)
  }

  return (
    <section className='container mc-py-10'>
      <div className='row mc-mb-10'>
        <div className='col-12 col-lg-10 offset-lg-1'>
          <p className='mc-text--uppercase mc-text-small mc-text--muted mc-text--bold mc-mb-4'>
            {course.categoriesLabel}
          </p>
          <h1 className='mc-text-h1 mc-mb-4'>{chapter.title}</h1>
          <div className='d-flex align-items-center mc-mb-6'>
            <Image
              alt=''
              className='mc-corners--circle'
              height={60}
              priority
              src={course.primary1x1}
              width={60}
            />
            <div className='mc-ml-4 mc-text--left'>
              <Link
                href={coursePath(course.slug)}
                className='mc-text--link'
                passHref
              >
                {course.instructorName}
              </Link>
              <p className='mc-mt-1 mc-text-small mc-text--muted'>
                {t('lesson.hero.chapterDuration', {
                  duration: chapter.duration,
                })}
              </p>
            </div>
          </div>
          <p>{chapter.abstract}</p>
        </div>
      </div>
      <Tile onClick={toggleTrailerModal}>
        <TileCaption position='center center'>
          <Icon kind='play-circle' className='mc-icon--8' />
        </TileCaption>
        <TileImage
          image={
            <Image
              alt={chapter.videoThumbUrlAltText}
              height={680}
              priority
              src={chapter.videoThumbUrl}
              width={1200}
            />
          }
        />
      </Tile>
      <div className='mc-theme-dark mc-bg-dark row justify-content-between align-items-start mc-m-0 mc-p-6'>
        <div className='col-sm-12 col-md-8'>
          <div className='d-flex justify-content-between align-items-center'>
            <p>
              <Trans ns='@mc/next' i18nKey='lesson.hero.ratingDescription'>
                <span className='d-none d-sm-inline' />
              </Trans>
            </p>
            <div className='flex-end'>
              <Ratings />
            </div>
          </div>
          <Separator className='mc-my-4' />
          <p className='mc-text-small mc-text--muted'>
            {t('lesson.hero.topics', {
              topics: chapter.topic,
            })}
          </p>
        </div>
        <ShareButton
          className='col-sm-12 col-md-3 c-button c-button--secondary c-button--md mc-bg-gray-200 mc-px-8 mc-my-4'
          {...getShareLessonProperties(course, chapter)}
        />
      </div>
      {isModalOpen && (
        <VideoModal
          show={isModalOpen}
          onCloseModal={toggleTrailerModal}
          canClickOutsideToClose={false}
          videoId={course.brightcoveVideoId}
          mediaUuid={course.mediaUuid}
          author={course.instructorName}
          tagline={course.headline}
          instructorAvatar={course.primary2x3}
          contentId={`${course.id}_${
            course.brightcoveVideoId ?? course.mediaUuid
          }`}
          contentTitle={course.title}
          contentType={TRACK_CONTENT_TYPES.COURSE_TRAILER}
          courseId={course.id}
          buttonGroup={
            <CTA
              trackProps={{
                category: 'need-to-know',
                content_type: 'product',
                location: 'trailer-modal',
                page_category: 'personalization',
              }}
            />
          }
        />
      )}
    </section>
  )
}

export default Hero
